import { DndContext, DragEndEvent, pointerWithin } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { EmptyListItem } from '../../typography/EmptyListItem';
import { DraggableListItem, DraggableListItemProps } from './DraggableListItem';
import { DraggableListItemInnerComponentProps } from './DraggableListItem.types';
import { measuringConfig, useDndKitSensors } from './SortableList';

export interface DragAndDropListProps<T> {
  items: T[];
  ItemInnerComponent: React.ComponentType<
    DraggableListItemInnerComponentProps<T>
  >;
  hasDragHandle?: boolean;
  withDividers?: boolean;
  droppableId?: string;
  onDropped?: (sortedList: T[]) => void;
  setValue?: UseFormSetValue<FieldValues>;
  respondToLengthChanges?: boolean;
  emptyStateMessage?: string;
  disabled?: boolean;
  /**
   * Optional custom class overrides for the list item styling.
   */
  itemClasses?: DraggableListItemProps<T>['classes'];
}

function transposeItemInList<T>(
  list: T[],
  fromIndex: number,
  toIndex: number
): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(fromIndex, 1);
  // Safe assertion: caller validates indices are >= 0 before calling
  result.splice(toIndex, 0, removed!);

  return result;
}

export function DragAndDropList<T extends { id: string; isGrouped?: boolean }>({
  items,
  ItemInnerComponent,
  hasDragHandle = true,
  withDividers = true,
  droppableId: _droppableId,
  onDropped,
  respondToLengthChanges = true,
  emptyStateMessage = 'No items',
  disabled = false,
  itemClasses,
}: DragAndDropListProps<T>) {
  const [sortedItems, setSortedItems] = useState(items);

  const sensors = useDndKitSensors();

  useEffect(
    function handleExternalItems() {
      setSortedItems((internalItems) => {
        const internalItemIds = internalItems.map((item) => item.id);

        // Sort items by the order of the internal items
        // If an item is not in the internal items, it will be sorted to the end
        const sorted = [...items].sort((a, b) => {
          const aIndex =
            internalItemIds.indexOf(a.id) === -1
              ? Infinity
              : internalItemIds.indexOf(a.id);
          const bIndex =
            internalItemIds.indexOf(b.id) === -1
              ? Infinity
              : internalItemIds.indexOf(b.id);

          return aIndex - bIndex;
        });

        return sorted;
      });
    },
    [items]
  );

  useEffect(() => {
    if (!respondToLengthChanges) {
      return;
    }

    // This reacts to items being added and removed from the list and
    // calls the onDropped callback to update the parent state
    onDropped?.(sortedItems);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only reacts to length changes
  }, [sortedItems.length]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Dropped outside the list
    if (!over) return;

    // No movement
    if (active.id === over.id) return;

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.id === over.id);

    // Guard against invalid indices - can occur if items change during drag
    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = transposeItemInList(sortedItems, oldIndex, newIndex);

    setSortedItems(newItems);
    onDropped?.(newItems);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        measuring={measuringConfig}
      >
        <SortableContext
          items={sortedItems.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {sortedItems.map((item, index) => (
              <DraggableListItem<T>
                item={item}
                index={index}
                key={item.id}
                ItemInnerComponent={ItemInnerComponent}
                isLast={index === sortedItems.length - 1}
                hasDragHandle={hasDragHandle}
                withDividers={withDividers}
                disabled={disabled}
                classes={itemClasses}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      {items.length === 0 && <EmptyListItem>{emptyStateMessage}</EmptyListItem>}
    </>
  );
}
